import { FC } from 'react';
import { NumberUtils, Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { Permission, Product, ProductCategoryType } from '@services';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type Entity = Product;

export interface IProductListProps extends IEntityListProps<Entity> {
	paddingX?: number | string;
	entities: Entity[];
	selected: Entity[];
	category: ProductCategoryType;
	onView?: (entity: Entity) => void;
	onSelectChange?: (selected: Entity[]) => void;
	onEdit?: (entity: Entity) => void;
	onDelete?: (entity: Entity) => void;
}

export const ProductList: FC<IProductListProps> = ({
	entities,
	selected,
	category,
	onView,
	onSelectChange,
	onEdit,
	onDelete,
	paddingX,
	...props
}) => {
	const user = useLoggedinUser();
	const hasWritePermission = user?.hasPermission(Permission.PRODUCT_WRITE);
	const handleSelectChange = (status: boolean, entity: Entity) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => {
				return (
					<EntityListItem
						key={`product-${entity.id}-${i}`}
						sx={{ paddingRight: paddingX }}
						selected={!!selected.find((s) => s.id === entity.id)}
						notSelectable={!hasWritePermission}
						actions={[
							{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !hasWritePermission },
							{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !hasWritePermission }
						]}
						onSelectChange={(status) => handleSelectChange(status, entity)}
					>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1.0 }}>
							<Grid container columnSpacing={2} sx={{ flexGrow: 1 }}>
								<Grid item xs={2}>
									<EntityListItemCell title={<Tr.Portal path="product.name" />}>
										<Typography>{entity.name || '-'}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={2}>
									<EntityListItemCell title={<Tr.Portal path="product.type" />}>
										<Typography>{entity.type || '-'}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="product.description" />}>
										<Typography>{entity.description || '-'}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={2}>
									<EntityListItemCell title={<Tr.Portal path="product.country" />}>
										<Typography>{entity.countryAsIsoStandard()?.countryName || '-'}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="product.price" />}>
										<Typography>
											{NumberUtils.formatAsPrice(entity.price, { prefix: entity.currency, pureDefault: true })}
										</Typography>
									</EntityListItemCell>
								</Grid>
							</Grid>
							{category === ProductCategoryType.CHARGING_POINT && (
								<IconButton onClick={() => onView?.(entity)}>
									<InfoOutlinedIcon />
								</IconButton>
							)}
						</Stack>
					</EntityListItem>
				);
			})}
		</EntityList>
	);
};
